<script lang="ts">
  import { onDestroy, onMount } from 'svelte'
  import { t } from '../lib/i18n.svelte'
  import Icon from '../lib/Icon.svelte'
  import { prefs } from '../lib/prefs.svelte'
  import {
    endOverlayInput,
    onOverlayInput,
    onOverlayLine,
    onOverlaySide,
    sendOverlayMessage,
    type OverlayLine,
    type OverlaySide,
  } from '../lib/tauri'

  /**
   * The in-game chat overlay. It lives in a transparent, click-through window
   * that the stream never captures, so it has to stay almost free: plain DOM,
   * a handful of lines, and fading done by a CSS animation rather than timers
   * or per-frame script. While nothing is said it does no work at all.
   */
  const MAX_LINES = 8
  const LIFETIME_MS = 12_000

  interface Row extends OverlayLine {
    id: number
  }

  let rows = $state<Row[]>([])
  let typing = $state(false)
  let side = $state<OverlaySide>(prefs.chatSide)
  let draft = $state('')
  let input = $state<HTMLInputElement | null>(null)
  let nextId = 0
  let unlisten: (() => void)[] = []

  onMount(async () => {
    document.documentElement.classList.add('overlay-window')
    unlisten = await Promise.all([
      onOverlayLine((line) => {
        const id = nextId++
        rows = [...rows.slice(-(MAX_LINES - 1)), { ...line, id }]
        setTimeout(() => {
          if (!typing) rows = rows.filter((row) => row.id !== id)
        }, LIFETIME_MS)
      }),
      onOverlaySide((next) => (side = next)),
      onOverlayInput((open) => {
        typing = open
        if (open) queueMicrotask(() => input?.focus())
        else draft = ''
      }),
    ])
  })

  onDestroy(() => unlisten.forEach((off) => off()))

  function close() {
    typing = false
    draft = ''
    void endOverlayInput().catch(() => {})
  }

  function onKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault()
      close()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const text = draft.trim()
      if (text) void sendOverlayMessage(text)
      close()
    }
  }
</script>

<div class="overlay" class:overlay--right={side === 'right'}>
  <ol class="overlay__list">
    {#each rows as row (row.id)}
      <li class="overlay__row" class:overlay__row--held={typing}>
        <span class="overlay__face" aria-hidden="true">{row.name.slice(0, 1).toUpperCase()}</span>
        <strong>{row.name}</strong>
        {#if row.item.kind === 'text'}
          {row.item.text}
        {:else if row.item.kind === 'react'}
          <span class="note-pop chat__note-icon--{row.item.reaction}"
            ><Icon name={row.item.reaction} size={28} /></span
          >
        {:else}
          <span class="note-pop chat__note-icon--hand" class:note-wave={row.item.up}
            ><Icon name="hand" size={28} />
            {row.item.up ? t('raised a hand') : t('lowered their hand')}</span
          >
        {/if}
      </li>
    {/each}
  </ol>
  {#if typing}
    <div class="chatfield">
      <span class="chatfield__who">{t('You')}</span>
      <input
        bind:this={input}
        bind:value={draft}
        onkeydown={onKey}
        maxlength={500}
        placeholder={t('Say something')}
        aria-label={t('Chat message')}
      />
      <span class="chatfield__keys"><kbd>Enter</kbd> {t('Send')} <kbd>Esc</kbd></span>
    </div>
  {/if}
</div>
